import { DXFData, DXFEntity } from '../new-project/dxf/types';

interface CodeValuePair {
  code: number;
  value: string;
}

export function parseDXF(text: string): DXFData {
  const lines = text.split(/\r?\n/);
  const pairs: CodeValuePair[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    pairs.push({ code: parseInt(lines[i].trim(), 10), value: lines[i + 1]?.trim() ?? '' });
  }

  const layers = new Set<string>();
  const entities: DXFEntity[] = [];
  let i = 0;

  // Find TABLES section to extract layers
  while (i < pairs.length) {
    if (pairs[i].code === 2 && pairs[i].value === 'LAYER') {
      i++;
      while (i < pairs.length) {
        if (pairs[i].code === 0 && pairs[i].value === 'ENDTAB') break;
        if (pairs[i].code === 0 && pairs[i].value === 'LAYER') {
          i++;
          while (i < pairs.length && pairs[i].code !== 0) {
            if (pairs[i].code === 2) layers.add(pairs[i].value);
            i++;
          }
          continue;
        }
        i++;
      }
    }
    if (pairs[i]?.code === 2 && pairs[i]?.value === 'ENTITIES') break;
    i++;
  }

  // Parse ENTITIES section
  const supportedTypes = ['LINE', 'LWPOLYLINE', 'POLYLINE', 'CIRCLE', 'ARC', 'ELLIPSE', 'SPLINE', '3DFACE'];

  while (i < pairs.length) {
    if (pairs[i].code === 0 && pairs[i].value === 'ENDSEC') break;

    if (pairs[i].code === 0) {
      const etype = pairs[i].value;
      if (supportedTypes.includes(etype)) {
        const entity: DXFEntity = {
          type: etype,
          layer: '0',
          vertices: [],
          closed: false,
          radius: 0,
          startAngle: 0,
          endAngle: 360,
          center: [0, 0],
        };
        i++;
        let cx = 0,
          cy = 0,
          vx: number | null = null,
          vy: number | null = null;

        while (i < pairs.length && pairs[i].code !== 0) {
          const c = pairs[i].code;
          const v = pairs[i].value;

          if (c === 8) entity.layer = v;
          if (c === 70 && (etype === 'LWPOLYLINE' || etype === 'POLYLINE')) {
            entity.closed = (parseInt(v) & 1) === 1;
          }

          if (etype === 'LWPOLYLINE') {
            if (c === 10) {
              if (vx !== null) entity.vertices.push([vx, vy || 0]);
              vx = parseFloat(v);
            }
            if (c === 20) {
              vy = parseFloat(v);
            }
          }

          if (etype === 'LINE') {
            if (c === 10) entity.vertices[0] = [parseFloat(v), entity.vertices[0]?.[1] || 0];
            if (c === 20) entity.vertices[0] = [entity.vertices[0]?.[0] || 0, parseFloat(v)];
            if (c === 11) entity.vertices[1] = [parseFloat(v), entity.vertices[1]?.[1] || 0];
            if (c === 21) entity.vertices[1] = [entity.vertices[1]?.[0] || 0, parseFloat(v)];
          }

          if (etype === 'CIRCLE' || etype === 'ARC') {
            if (c === 10) cx = parseFloat(v);
            if (c === 20) cy = parseFloat(v);
            if (c === 40) entity.radius = parseFloat(v);
            if (c === 50) entity.startAngle = parseFloat(v);
            if (c === 51) entity.endAngle = parseFloat(v);
          }

          i++;
        }

        if (etype === 'LWPOLYLINE' && vx !== null) {
          entity.vertices.push([vx, vy || 0]);
        }

        if (etype === 'CIRCLE' || etype === 'ARC') {
          entity.center = [cx, cy];
          const segments = 32;
          const sa = (entity.startAngle * Math.PI) / 180;
          const ea = etype === 'CIRCLE' ? sa + Math.PI * 2 : (entity.endAngle * Math.PI) / 180;
          for (let s = 0; s <= segments; s++) {
            const a = sa + (ea - sa) * (s / segments);
            entity.vertices.push([cx + Math.cos(a) * entity.radius, cy + Math.sin(a) * entity.radius]);
          }
          if (etype === 'CIRCLE') entity.closed = true;
        }

        if (entity.vertices.length >= 2) {
          layers.add(entity.layer);
          entities.push(entity);
        }

        continue;
      }
    }
    i++;
  }

  return { layers: [...layers].sort(), entities };
}
